import { type AuthToken } from '@reliance/types/models/Auth';
import { EventEmitter } from 'events';
import { type Socket } from 'socket.io-client';
import config from '../services/config';
import { store } from '../store/store';

let instance: NodeServer | null = null;

type NodeServerCallback = (
  connected: boolean,
  socket: Socket | null,
  connectAttempts?: number,
) => void;

export type { NodeServerCallback };

type SocketPacket = {
  data: [event: string, payload?: unknown];
};

type LegacySocket = {
  onevent: (packet: SocketPacket) => void;
};

class NodeServer extends EventEmitter {
  isConnected: boolean = false;
  connectAttempts: number = 0;
  socket: Socket | null = null;
  connectionListeners: NodeServerCallback[] = [];

  constructor() {
    super();
    this.handleDisconnect = this.handleDisconnect.bind(this);
    this.connectToNode = this.connectToNode.bind(this);
    this.addConnectionListener = this.addConnectionListener.bind(this);
    this.onConnect = this.onConnect.bind(this);
    this.handleError = this.handleError.bind(this);
  }

  // Singleton instance getter
  static get(): NodeServer {
    if (instance == null) {
      instance = new this();
      instance.init();
    }

    return instance;
  }

  // Initialize the line monitor
  init() {
    this.isConnected = false;
    this.connectAttempts = 0;

    this.connectionListeners = [];
    this.socket = null;

    setTimeout(() => {
      this.connectToNode();
    }, 500);

    return this;
  }

  // Handle a disconnection from the node server
  handleDisconnect() {
    console.log(
      'NodeServer: Connection to server failed or disconnected. Attempting reconnect',
    );
    for (const callback of this.connectionListeners) {
      callback(false, this.socket, this.connectAttempts);
    }
    this.isConnected = false;
  }

  // Attempt to connect to the node server
  connectToNode() {
    console.log('NodeServer: connecting');
    if (config.nodeServer == null) {
      console.log('NodeServer: disabled');
      return this;
    }

    if (this.socket) {
      console.log('NodeServer: socket exists');
      return this;
    }

    this.connectAttempts++;
    console.log(
      `NodeServer: Connecting to server ${config.nodeServer} (${this.connectAttempts})`,
    );

    const appState = store.getState();

    if (appState.common?.facility?.id == null) {
      console.log('NodeServer: awaiting facility data');
      return this;
    }

    if (!window.io) {
      console.log('NodeServer: socket.io not loaded');
      return this;
    }

    this.socket = window.io.connect(config.nodeServer, {
      timeout: 6000,

      // Prefer websocket to avoid long-polling. Keep polling as fallback
      transports: ['websocket', 'polling'],

      // Allow socket.io to reconnect up to 10 seconds after a disconnect (default was 5s)
      // It'll automatically back off up to this delay
      reconnectionDelayMax: 10000,

      // Include the facility ID in the query string
      query: {
        'X-Facility-ID': appState.common.facility.id,
      },
    });

    this.socket?.on('error', (err) => {
      console.log(`NodeServer: Socket.IO error: ${err}`);
      if (!this.isConnected) {
        this.handleDisconnect();
      }
    });
    this.socket?.on('connect', this.onConnect);
    this.socket?.on('connect_failed', this.handleDisconnect);
    this.socket?.on('reconnect_failed', this.handleDisconnect);
    this.socket?.on('reconnecting', () => {
      console.log(
        'NodeServer: Connection to server dropped. Automatic reconnection attempt',
      );
      for (const callback of this.connectionListeners) {
        callback(false, this.socket, this.connectAttempts);
      }
      this.connectAttempts++;
      this.isConnected = false;
    });
    this.socket?.on('station-status.change', (data) =>
      this.emit('station-status.change', data),
    );
    this.socket?.on('station-status.list', (data) =>
      this.emit('station-status.list', data),
    );

    // Relay all events to the store
    const socket = this.socket;
    if (!socket) {
      return this;
    }

    const legacySocket = socket as unknown as LegacySocket;
    const originalOnevent = legacySocket.onevent;
    legacySocket.onevent = (packet: SocketPacket) => {
      store.dispatch({
        type: `NODE_SERVER/${packet.data[0].toUpperCase()}`,
        data: packet.data.length > 1 ? packet.data[1] : null,
      });

      // Relay the original event
      originalOnevent.call(socket, packet);
    };

    return this.socket;
  }

  // Handle a successful socket connection (including reconnects)
  onConnect() {
    this.isConnected = true;
    this.connectAttempts = 0;

    console.log('NodeServer: Connected to server');
    for (const callback of this.connectionListeners) {
      callback(true, this.socket!);
    }

    // Re-auth on every connect so reconnects do not stay unauthenticated
    const authState = store.getState().auth;
    if (authState.loggedIn && authState.token) {
      this.authenticate(authState.token as AuthToken);
    }
  }

  // Add a listener for connection status changes
  addConnectionListener(callback: NodeServerCallback) {
    if (this.isConnected && this.socket) {
      callback(true, this.socket);
    }

    this.connectionListeners.push(callback);
    return this;
  }

  // Remove a listener for connection status changes
  removeConnectionListener(callback: NodeServerCallback) {
    this.connectionListeners.splice(
      this.connectionListeners.indexOf(callback),
      1,
    );
    return this;
  }

  // Handle the socket error
  handleError() {
    return this;
  }

  authenticate(accessToken: AuthToken) {
    if (!this.socket) {
      this.connectToNode();
    } else {
      this.socket.emit('authenticate', `Bearer ${accessToken.accessToken}`);
    }
  }

  disconnect() {
    if (!this.socket) {
      return;
    }

    // Let the server know we're logging out
    this.socket.emit('unauthenticate');

    // Close the socket so logout does not leave us reconnecting
    this.socket.disconnect();

    // Allow a later login to create a fresh connection and authenticate.
    this.socket = null;

    this.isConnected = false;
  }
}

export default NodeServer;
